/**
 * Runtime / DSL / contract Zod schemas. Mirrors IMPLEMENTATION_CONTRACT §4.3
 * (the DSL/runtime block) and §9, plus `specs/main.spec.md §7 / §9 / §10`.
 *
 * These validate things that are not plain CRUD rows: rulebase YAML rules and
 * processes, the §9 Context Packet, the canonical Simple Task Agent output
 * contract, the event-intake body, the agent callback body, and the access
 * check body.
 */

import { z } from 'zod';

import {
  ARTIFACT_TYPES,
  AGENT_RUN_STATUS,
  ACTIONS,
  APPROVAL_DECISIONS,
  CONFIDENTIALITY,
  CONTEXT_MATURITY_STAGES,
  CONTEXT_POLICIES,
  CONTEXT_TYPES,
  CRYSTALLIZATION_STAGES,
  EFFECT_RUN_STATUS,
  EFFECT_TYPES,
  EVENT_OUTBOX_STATUS,
  EVENT_TYPES,
  EXECUTOR_TYPES,
  RULE_STATUS,
  TRIGGER_EVENT_TYPES,
} from '../constants/enums';

export * from './package-runtime';
export * from './contour-runtime';
export * from './strategy-execution';
export * from './project-control-tower';
export * from './actor-authority';
export * from './access-runtime';
export * from './action-cards';
export * from './decision-room';
export * from './skill-workspace';
export * from './agent-profiles';
export * from './view-runtime';

// --- Rulebase rule DSL (`ruleDefinitionSchema`) ----------------------------
// Validates a rulebase YAML rule. Every field maps to a `rules` table column
// (see contract §14 / spec §7). The canonical first rule is
// `task.created.dispatch-simple-agent`.

export const ruleDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  status: z.enum(RULE_STATUS),
  version: z.number().int().optional(),
  trigger_event_type: z.enum(TRIGGER_EVENT_TYPES),
  artifact_type: z.enum(ARTIFACT_TYPES).optional(),
  required_status: z.string().optional(),
  required_phase_id: z.string().optional(),
  required_evidence_type: z.string().optional(),
  required_evidence_count: z.number().int().optional(),
  effect_type: z.enum(EFFECT_TYPES),
  target_status: z.string().optional(),
  target_phase_id: z.string().optional(),
  target_skill_id: z.string().optional(),
  executor_type: z.enum(EXECUTOR_TYPES).optional(),
  emitted_event_type: z.enum(EVENT_TYPES).optional(),
  priority: z.number().int().optional(),
});
export type RuleDefinition = z.infer<typeof ruleDefinitionSchema>;

// --- Rulebase process DSL (`processDefinitionSchema`) ----------------------
// Validates a rulebase process YAML: phases + transition rules (contract §14).

export const processPhaseDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sort_order: z.number().int().optional(),
  purpose: z.string().optional(),
  is_start: z.boolean().optional(),
  is_terminal: z.boolean().optional(),
});
export type ProcessPhaseDefinition = z.infer<typeof processPhaseDefinitionSchema>;

export const processTransitionDefinitionSchema = z.object({
  id: z.string().min(1),
  from_phase: z.string().min(1),
  to_phase: z.string().min(1),
  rule_id: z.string().min(1),
  label: z.string().optional(),
});
export type ProcessTransitionDefinition = z.infer<typeof processTransitionDefinitionSchema>;

export const processDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  domain: z.string().optional(),
  artifact_type: z.enum(ARTIFACT_TYPES).optional(),
  rigidity_level: z.number().int().optional(),
  purpose: z.string().optional(),
  phases: z.array(processPhaseDefinitionSchema),
  transitions: z.array(processTransitionDefinitionSchema),
});
export type ProcessDefinition = z.infer<typeof processDefinitionSchema>;

// --- Context Packet (`contextPacketSchema`) --------------------------------
// The reproducible §9 snapshot stored to R2 at
// `context-packets/{rule_run_id}.json`. Optional sub-objects mirror the spec
// example, which only includes the slices relevant to a given execution.

export const contextPacketSchema = z.object({
  context_packet_id: z.string().min(1),
  created_at: z.string().min(1),
  event: z.object({
    id: z.string(),
    event_type: z.enum(EVENT_TYPES),
  }),
  artifact: z
    .object({
      id: z.string(),
      artifact_type: z.enum(ARTIFACT_TYPES),
      title: z.string(),
      status: z.string(),
    })
    .optional(),
  task: z
    .object({
      instruction: z.string().nullable().optional(),
    })
    .optional(),
  project: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  rule: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  skill: z
    .object({
      id: z.string(),
      name: z.string(),
      body_markdown: z.string().nullable().optional(),
    })
    .optional(),
  evidence: z.array(z.unknown()).default([]),
  claims: z.array(z.unknown()).default([]),
  documents: z.array(z.unknown()).default([]),
  constraints: z.array(z.string()).default([]),
  output_contract: z.record(z.string(), z.unknown()).optional(),
});
export type ContextPacket = z.infer<typeof contextPacketSchema>;

// --- Context resolve input (`contextResolveInputSchema`) -------------------
// Body the Context Resolver (CONTRACT_V2 §5.1) consumes to produce a v0.2
// packet. Used by `GET /api/contexts/:id/resolve` (preview path) and the kernel
// (which synthesizes it from the rule run). All fields optional so a minimal
// preview input parses; the resolver fills sensible defaults.

export const contextResolveInputSchema = z.object({
  artifact_id: z.string().optional(),
  context_id: z.string().optional(),
  actor_id: z.string().optional(),
  actor_roles: z.array(z.string()).optional(),
  rule_id: z.string().optional(),
  target_package_id: z.string().optional(),
  action_type: z.string().optional(),
  risk_level: z.enum(['low', 'medium', 'high']).optional(),
  context_policy: z.enum(CONTEXT_POLICIES).optional(),
});
export type ContextResolveInput = z.infer<typeof contextResolveInputSchema>;

// --- Context Packet v0.2 (`contextPacketV2Schema`) -------------------------
// CONTRACT_V2 §6 — a SUPERSET of the v0.1 packet. Every v2-only field is
// `.optional()` / `.default()`-ed so this schema `.parse()`s a v0.1 packet
// unchanged (forward-compatible storage format). The resolver produces this
// richer shape; the v0.1 `assembleContextPacket` still produces the v0.1 shape.

const scoredContextSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(CONTEXT_TYPES).optional(),
  maturity_stage: z.enum(CONTEXT_MATURITY_STAGES).optional(),
  score: z.number().optional(),
});

export const contextPacketV2Schema = z.object({
  context_packet_id: z.string().min(1),
  version: z.string().default('0.2'),
  policy: z.enum(CONTEXT_POLICIES).default('standard'),
  created_at: z.string().min(1),
  coverage: z.number().min(0).max(1).default(1),

  subject: z
    .object({
      artifact_id: z.string(),
      artifact_type: z.enum(ARTIFACT_TYPES).optional(),
      title: z.string().optional(),
    })
    .nullable()
    .optional(),

  event: z.object({
    id: z.string(),
    event_type: z.enum(EVENT_TYPES),
  }),

  active_contexts: z.array(scoredContextSchema).default([]),
  root_context_id: z.string().nullable().optional(),
  resolved_context_ids: z.array(z.string()).default([]),
  resolver_policy: z.enum(CONTEXT_POLICIES).optional(),
  scoring_profile_version: z.string().optional(),
  source_watermark: z.string().optional(),
  access_fingerprint: z.string().optional(),

  selected_packages: z
    .object({
      skill: z.string().nullable().optional(),
      contour: z.string().nullable().optional(),
      frames: z.array(z.string()).default([]),
      principles: z.array(z.string()).default([]),
      bets: z.array(z.string()).default([]),
    })
    .optional(),

  artifact: z
    .object({
      id: z.string(),
      artifact_type: z.enum(ARTIFACT_TYPES).optional(),
      title: z.string().optional(),
      status: z.string().optional(),
      instruction: z.string().nullable().optional(),
      primary_context_id: z.string().nullable().optional(),
      crystallization_stage: z.enum(CRYSTALLIZATION_STAGES).optional(),
      confidentiality: z.enum(CONFIDENTIALITY).optional(),
    })
    .optional(),

  task: z
    .object({
      instruction: z.string().nullable().optional(),
    })
    .optional(),
  project: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  rule: z
    .object({
      id: z.string(),
      name: z.string(),
      context_policy: z.enum(CONTEXT_POLICIES).optional(),
    })
    .optional(),
  skill: z
    .object({
      id: z.string(),
      name: z.string(),
      body_markdown: z.string().nullable().optional(),
    })
    .optional(),

  evidence: z.array(z.unknown()).default([]),
  claims: z.array(z.unknown()).default([]),
  documents: z.array(z.unknown()).default([]),
  signals: z.array(z.unknown()).default([]),

  constraints: z.array(z.string()).default([]),
  output_contract: z.record(z.string(), z.unknown()).optional(),

  // Milestone A (IMPL-A-CONTRACT §5.2/§5.3) — the advisory AccessGuard trace.
  // Each entry records one source/context the resolver-on-dispatch AccessGuard
  // would additionally drop for the dispatch actor and why. Optional + defaulted
  // so a v0.1/v0.2 packet (no `redactions` field) still `.parse()`s unchanged,
  // and a packet carrying entries validates either way (additive, back-compat).
  redactions: z
    .array(
      z.object({
        entity_type: z.string(),
        entity_id: z.string(),
        reason: z.enum([
          'access_denied',
          'confidentiality_ceiling',
          'binding_none',
          'out_of_context_scope',
        ]),
      }),
    )
    .default([]),
});
export type ContextPacketV2 = z.infer<typeof contextPacketV2Schema>;

// --- Skill output contract (`skillOutputContractSchema`) -------------------
// The canonical Simple Task Agent output (contract §4.3 / spec §10).

export const skillOutputContractSchema = z.object({
  result_summary: z.string(),
  confidence: z.number(),
  next_step: z.string(),
  needs_clarification: z.boolean(),
});
export type SkillOutputContract = z.infer<typeof skillOutputContractSchema>;

// --- Event intake (`eventInputSchema` + `createEventSchema`) ---------------
// Body accepted by `POST /api/events` and used by the EventBus. `events` are
// append-only, so the events resource's create schema is exactly this shape.

export const eventInputSchema = z.object({
  event_type: z.enum(EVENT_TYPES),
  artifact_id: z.string().optional(),
  source: z.string().optional(),
  payload: z.unknown().optional(),
  correlation_id: z.string().optional(),
  causation_id: z.string().optional(),
});
export type EventInput = z.infer<typeof eventInputSchema>;

/** Events are append-only; their CRUD "create" is the intake body. */
export const createEventSchema = eventInputSchema;
export type CreateEventInput = EventInput;

// --- Agent callback (`agentCallbackSchema`) --------------------------------
// Body of `POST /api/agent-runs/:id/callback`.

export const agentCallbackSchema = z.object({
  status: z.enum(AGENT_RUN_STATUS),
  output_summary: z.string().optional(),
  output_url: z.string().optional(),
  external_run_id: z.string().optional(),
  error: z.string().optional(),
});
export type AgentCallbackInput = z.infer<typeof agentCallbackSchema>;

// --- Access check (`accessCanSchema`) --------------------------------------
// Body of `POST /api/access/can`.

export const accessCanSchema = z.object({
  resource: z.string().min(1),
  action: z.enum(ACTIONS),
  recordId: z.string().optional(),
});
export type AccessCanInput = z.infer<typeof accessCanSchema>;

// --- Kernel runtime contracts (technical/04 §4 / §8.5) ---------------------
// Row + request shapes the kernel/worker slices (S5/S6) import. The DDL lives in
// the kernel migration slice; these are the shared validation contracts so the
// outbox publisher, effect runner, and approval path agree on one shape.

/**
 * The `event_outbox` row — the durable publish obligation written in the SAME D1
 * transaction as the business mutation + domain event (technical/04 §4). The
 * publisher reads `pending`, publishes to EVENT_QUEUE, then marks `published`.
 * `status` domain is the canonical `EVENT_OUTBOX_STATUS` (S1 enums).
 */
export const eventOutboxRowSchema = z.object({
  id: z.string().min(1),
  event_id: z.string().min(1),
  status: z.enum(EVENT_OUTBOX_STATUS).default('pending'),
  attempts: z.number().int().min(0).default(0),
  next_attempt_at: z.string().min(1).nullable().optional(),
  last_error: z.string().min(1).nullable().optional(),
  created_at: z.string().min(1),
  processed_at: z.string().min(1).nullable().optional(),
});
export type EventOutboxRow = z.infer<typeof eventOutboxRowSchema>;

/**
 * The `effect_runs` row (technical/03 §10 column contract; technical/04 §8.5) —
 * per-effect idempotency and retry state so one failed target does not duplicate
 * all targets. `idempotency_key` = `rule_run_id + effect_type + target_id`.
 * `status` domain is the canonical `EFFECT_RUN_STATUS` (S1 enums; mirrors
 * `RULE_RUN_STATUS`).
 */
export const effectRunSchema = z.object({
  id: z.string().min(1),
  rule_run_id: z.string().min(1),
  effect_type: z.enum(EFFECT_TYPES),
  target_type: z.string().min(1).nullable().optional(),
  target_id: z.string().min(1).nullable().optional(),
  idempotency_key: z.string().min(1),
  status: z.enum(EFFECT_RUN_STATUS).default('running'),
  attempt_count: z.number().int().min(0).default(0),
  last_error: z.string().min(1).nullable().optional(),
  result_json: z.string().min(1).nullable().optional(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1).nullable().optional(),
});
export type EffectRun = z.infer<typeof effectRunSchema>;

/**
 * Body of an approval decision (technical/06 / §approval_decisions). One vote on a
 * parked effect: approve, reject, or cancel, with an optional rationale recorded
 * on the `approval_decisions` row. `decision` reuses the canonical
 * `APPROVAL_DECISIONS` enum so the access/policy path and this request agree.
 */
export const approvalDecisionRequestSchema = z.object({
  decision: z.enum(APPROVAL_DECISIONS),
  rationale: z.string().min(1).optional(),
});
export type ApprovalDecisionRequest = z.infer<typeof approvalDecisionRequestSchema>;
